import { cn } from '@repo/ui'
import { type QueryClient, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Outlet, useParams } from '@tanstack/react-router'

import { AppHeader } from '@/components/app-header'
import { ConversationListPanel } from '@/features/inbox/conversation-list-panel'
import { validateConversasSearch } from '@/features/inbox/conversation-list-search'
import { useAuth } from '@/hooks/use-auth'
import type { ConversationsPage } from '@/hooks/use-conversations'
import { useInboxSocket } from '@/hooks/use-inbox-socket'
import { useMessageSound } from '@/hooks/use-message-sound'
import { isChannelNotifyEnabled } from '@/hooks/use-notification-prefs'
import type { ConversationRow } from '@/types/inbox'

export const Route = createFileRoute('/_app/conversas')({
  validateSearch: validateConversasSearch,
  component: ConversasLayout,
})

interface InfiniteConversations {
  pages?: ConversationsPage[]
}

function findConversationChannelId(
  qc: QueryClient,
  orgId: string | null,
  conversationId: string
): string | null {
  const single = qc.getQueryData<ConversationRow>([
    'conversation',
    orgId,
    conversationId,
  ])
  if (single?.channelId) return single.channelId
  const lists = qc.getQueriesData<InfiniteConversations>({
    queryKey: ['conversations', orgId],
  })
  for (const [, data] of lists) {
    for (const page of data?.pages ?? []) {
      const row = page.data.find((item) => item.id === conversationId)
      if (row) return row.channelId
    }
  }
  return null
}

function ConversasLayout() {
  const { currentOrgId } = useAuth()
  const { conversationId } = useParams({ strict: false })
  const inChat = Boolean(conversationId)
  const qc = useQueryClient()
  const { play } = useMessageSound()

  // Single socket for the whole conversations shell; the chat route must not
  // open its own connection.
  useInboxSocket(currentOrgId, {
    onMessageNew: (message, messageConversationId) => {
      if (message.direction !== 'inbound') return
      // Conversa aberta e aba visível → o usuário já está vendo a mensagem.
      if (
        messageConversationId === conversationId &&
        document.visibilityState === 'visible'
      ) {
        return
      }
      const channelId = findConversationChannelId(
        qc,
        currentOrgId,
        messageConversationId
      )
      if (channelId && !isChannelNotifyEnabled(channelId)) return
      play()
    },
  })

  return (
    <div className="relative flex min-h-0 flex-1 overflow-hidden">
      {/* List column stays mounted across navigation. On mobile the chat
          overlays it; `invisible` (not `hidden`) keeps the list scrollTop. */}
      <div
        className={cn(
          'border-border flex min-h-0 w-full flex-col lg:w-80 lg:shrink-0 lg:border-r xl:w-90',
          inChat && 'invisible lg:visible'
        )}
      >
        <AppHeader title="Conversas" />
        <ConversationListPanel activeConversationId={conversationId ?? null} />
      </div>

      <div
        className={cn(
          'min-h-0 min-w-0',
          inChat
            ? 'bg-background absolute inset-0 z-10 flex lg:relative lg:inset-auto lg:z-auto lg:flex-1'
            : 'hidden lg:flex lg:flex-1'
        )}
      >
        <Outlet />
      </div>
    </div>
  )
}
